import { useState, useEffect } from 'react';
import { createCustomerETLApi } from '../api/etlServiceApi';

const CRON_PRESETS = [
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Every 4 hours', value: '0 */4 * * *' },
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Daily at midnight', value: '0 0 * * *' },
  { label: 'Daily at 6am', value: '0 6 * * *' },
  { label: 'Weekly (Monday 6am)', value: '0 6 * * 1' },
  { label: 'Custom', value: '' },
];

const INTERVAL_UNITS = [
  { label: 'minutes', minutesPerUnit: 1 },
  { label: 'hours', minutesPerUnit: 60 },
  { label: 'days', minutesPerUnit: 1440 },
];

export default function ETLScheduleConfig({ customerId, schedule, open, onToggle, onSaved }) {
  const [mode, setMode] = useState('cron');
  const [cron, setCron] = useState('');
  const [customCron, setCustomCron] = useState('');
  const [startAt, setStartAt] = useState('');
  const [intervalValue, setIntervalValue] = useState(1);
  const [intervalUnit, setIntervalUnit] = useState('hours');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!schedule) return;
    setMode(schedule.mode === 'interval' ? 'interval' : 'cron');

    if (schedule.cron_expression) {
      const preset = CRON_PRESETS.find(p => p.value === schedule.cron_expression);
      if (preset && preset.value) {
        setCron(schedule.cron_expression);
      } else {
        setCron('');
        setCustomCron(schedule.cron_expression);
      }
    }

    if (schedule.interval_minutes) {
      const unit = [...INTERVAL_UNITS].reverse().find(
        u => schedule.interval_minutes % u.minutesPerUnit === 0,
      ) ?? INTERVAL_UNITS[0];
      setIntervalUnit(unit.label);
      setIntervalValue(schedule.interval_minutes / unit.minutesPerUnit);
    }
    if (schedule.start_at) {
      // Trim to the "YYYY-MM-DDTHH:mm" shape a datetime-local input expects.
      setStartAt(String(schedule.start_at).slice(0, 16));
    }
  }, [schedule]);

  const effectiveCron = cron || customCron;

  const handleSave = async () => {
    if (!customerId) return;
    setError(null);
    setSaving(true);
    try {
      const api = createCustomerETLApi(customerId);
      const payload =
        mode === 'cron'
          ? { mode: 'cron', cron_expression: effectiveCron, enabled: true }
          : {
              mode: 'interval',
              interval_minutes:
                Number(intervalValue) *
                (INTERVAL_UNITS.find(u => u.label === intervalUnit)?.minutesPerUnit ?? 1),
              // APScheduler's IntervalTrigger fires one full interval after
              // start_date, not immediately — so an empty "start at" needs
              // to resolve to "now" here to actually mean "start right away".
              start_at: startAt || new Date().toISOString().slice(0, 16),
              enabled: true,
            };
      await api.setSchedule(payload);
      setSaved(true);
      onSaved?.();
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err?.response?.data?.detail ?? err.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const canSave = mode === 'cron' ? !!effectiveCron : !!intervalValue && Number(intervalValue) > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Configure Schedule
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-gray-100 space-y-3 pt-3">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setMode('cron')}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                mode === 'cron' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Cron expression
            </button>
            <button
              onClick={() => setMode('interval')}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                mode === 'interval' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Start time + interval
            </button>
          </div>

          {mode === 'cron' ? (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Preset</label>
                <select
                  value={cron}
                  onChange={e => {
                    setCron(e.target.value);
                    if (e.target.value) setCustomCron('');
                  }}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {CRON_PRESETS.map(p => (
                    <option key={p.label} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              {!cron && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Custom cron expression
                  </label>
                  <input
                    type="text"
                    value={customCron}
                    onChange={e => setCustomCron(e.target.value)}
                    placeholder="e.g. 0 */4 * * *"
                    className="w-full text-sm font-mono border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}

              {effectiveCron && (
                <div className="text-xs text-gray-400 bg-gray-50 rounded px-2 py-1.5 font-mono">
                  {effectiveCron}
                </div>
              )}
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Start at
                </label>
                <input
                  type="datetime-local"
                  value={startAt}
                  onChange={e => setStartAt(e.target.value)}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-400 mt-1">Leave empty to start immediately.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Repeat every
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="1"
                    value={intervalValue}
                    onChange={e => setIntervalValue(e.target.value)}
                    className="w-24 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <select
                    value={intervalUnit}
                    onChange={e => setIntervalUnit(e.target.value)}
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {INTERVAL_UNITS.map(u => (
                      <option key={u.label} value={u.label}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          <div className="pt-1">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Transformer</label>
            <input
              type="text"
              readOnly
              value={schedule?.transformer_id ?? 'ethen_warehouse'}
              className="w-full text-sm border border-gray-100 rounded-lg px-3 py-2 bg-gray-50 text-gray-500 focus:outline-none"
            />
            <p className="text-xs text-gray-400 mt-1">
              Set via <code className="bg-gray-100 px-1 rounded">TRANSFORMER_ID</code> environment variable.
            </p>
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 rounded px-2 py-1.5">{error}</p>
          )}

          <button
            onClick={handleSave}
            disabled={!canSave || saving || !customerId}
            className="w-full py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Schedule'}
          </button>
        </div>
      )}
    </div>
  );
}
